import React from "react";
import { Briefcase, TrendingUp, AlertTriangle, Repeat } from "lucide-react";
import { motion } from "framer-motion";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { SERVER_URL } from "../../../../../utils/serverConfig";
import { Sk, sentenceLines } from "./storyBits";

/* Numbers are what a scanner reads first — set them in stronger ink.
   "7h 24m", "1,304", "2h 23m", "68%" all get the treatment. */
function emphasiseNumbers(line, t) {
  const parts = String(line).split(/(\d[\d,.]*\s?(?:h\b|m\b|%|)(?:\s?\d+m\b)?)/g);
  return parts.map((p, i) =>
    /^\d/.test(p)
      ? <b key={i} style={{ color: t.c.text, fontWeight: t.font.weight.semibold, fontVariantNumeric: "tabular-nums" }}>{p}</b>
      : p);
}

/* The AI brief as a swipeable rail of takeaway cards. Streams from
   /day-brief (server-cached per person+day+source); hovering a card lights
   the stretches it anchors to on the spine above, clicking opens the drawer. */

/* `icon` is what the drawer header's tinted tile renders — a takeaway has no
   application of its own, so the kind itself has to carry the glyph. */
export const KIND_META = (t) => ({
  work: { tag: "Work", color: t.c.ai, icon: Briefcase },
  win: { tag: "Win", color: t.status.positive, icon: TrendingUp },
  risk: { tag: "Watch", color: t.status.warning, icon: AlertTriangle },
  pattern: { tag: "Pattern", color: t.c.primary, icon: Repeat },
});

export function useDayBrief(userId, date, source) {
  const [takeaways, setTakeaways] = React.useState([]);
  const [summary, setSummary] = React.useState("");
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [empty, setEmpty] = React.useState(false);
  const abortRef = React.useRef(null);
  const ranFor = React.useRef(null);
  const forceRef = React.useRef(false);
  const [kick, setKick] = React.useState(0);

  React.useEffect(() => {
    abortRef.current?.abort();
    setTakeaways([]); setSummary(""); setError(null); setEmpty(false); setRunning(false);
  }, [userId, date, source]);

  React.useEffect(() => () => abortRef.current?.abort(), []);

  const regenerate = React.useCallback(() => {
    if (!userId || !date) return;
    forceRef.current = true;
    ranFor.current = null;
    setTakeaways([]); setSummary(""); setError(null); setEmpty(false);
    setKick((n) => n + 1);
  }, [userId, date]);

  React.useEffect(() => {
    if (!userId || !date) return;
    const key = `${userId}|${date}|${source}|${kick}`;
    if (ranFor.current === key) return;   // dev StrictMode double-invoke guard
    ranFor.current = key;
    const force = forceRef.current;
    forceRef.current = false;

    (async () => {
      setRunning(true); setError(null);
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/day-brief`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ userId, date, source, force }),
          signal: abortRef.current.signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (err?.error?.code === "AI_LIMIT_REACHED") throw new Error("AI limit reached for this organisation.");
          throw new Error(err?.message || "Could not summarise this day.");
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let d; try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
            if (d.type === "takeaway") setTakeaways((cur) => [...cur, d.takeaway]);
            else if (d.type === "summary") setSummary(d.summary || "");
            else if (d.type === "empty") setEmpty(true);
            else if (d.type === "error") setError(d.message);
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Could not summarise this day.");
      } finally {
        setRunning(false);
      }
    })();
  }, [userId, date, source, kick]);

  return { takeaways, summary, running, error, empty, regenerate };
}

/** The stretches a takeaway anchors to — used to light the spine. */
export function takeawayMarks(tk) {
  return tk.occupiesTime === false
    ? (tk.demonstration || []).map((d) => d.startMs)
    : (tk.anchors || []).flatMap((a) => (a.stretches || []).map((s) => s.startMs));
}

function noteMarks(tk) {
  if (tk.shape?.windowFrom && tk.shape?.windowTo) {
    return [{ startMs: tk.shape.windowFrom, endMs: tk.shape.windowTo }];
  }
  const fromAnchors = (tk.anchors || []).flatMap((a) => a.stretches || []);
  if (fromAnchors.length) return fromAnchors;
  return (tk.demonstration || []).map((d) => ({
    startMs: d.startMs, endMs: d.endMs || (d.startMs + 20 * 60000),
  }));
}

function NoteRibbon({ tk, bounds, color }) {
  if (!bounds?.startMs || !bounds?.endMs) return null;
  const marks = noteMarks(tk).filter((s) => s.startMs && s.endMs);
  if (!marks.length) return null;
  const span = Math.max(1, bounds.endMs - bounds.startMs);
  return (
    <span aria-hidden="true" style={{
      display: "block", position: "relative", height: 4, borderRadius: 99,
      overflow: "hidden", marginTop: 10, background: color + (color.startsWith("#") ? "22" : ""),
    }}>
      {marks.map((s, j) => (
        <span key={j} style={{
          position: "absolute", top: 0, bottom: 0, borderRadius: 99, background: color,
          left: `${Math.max(0, ((s.startMs - bounds.startMs) / span) * 100)}%`,
          width: `${Math.max(1.6, ((s.endMs - s.startMs) / span) * 100)}%`,
        }} />
      ))}
    </span>
  );
}

export function StoryRail({ brief, onFocusStretches, onOpen, nested, bounds }) {
  const t = useDashTheme();
  const meta = KIND_META(t);
  const { takeaways, summary, running, error, empty } = brief;
  const lines = sentenceLines(summary);
  const lead = lines[0] || "";
  const rest = lines.slice(1);

  if (error) {
    return (
      <div style={{
        fontSize: t.font.size.base, color: t.c.textSecondary, background: t.c.surface,
        border: `1px solid ${t.c.border}`, borderRadius: t.r.md, padding: `${t.s[4]}px ${t.s[5]}px`,
      }}>{error}</div>
    );
  }
  if (empty) return null;

  const featIdx = takeaways.findIndex((tk) => tk.kind === "work");
  const featuredAt = featIdx >= 0 ? featIdx : 0;

  return (
    <div>
      {lead && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          style={{
            padding: "20px 22px 18px",
            background: t.isDark
              ? `linear-gradient(180deg, ${t.c.ai}1f 0%, ${t.c.surface} 72%)`
              : `linear-gradient(180deg, ${t.c.ai}12 0%, #fff 70%)`,
            borderBottom: `1px solid ${t.c.border}`,
          }}
        >
          <div style={{
            fontSize: 10.5, fontWeight: 750, letterSpacing: "0.12em", textTransform: "uppercase",
            color: t.c.ai, marginBottom: 10,
          }}>The day in one line</div>
          <div style={{
            fontSize: 18, fontWeight: 550, letterSpacing: "-0.025em", lineHeight: 1.4,
            color: t.c.text, maxWidth: 640,
          }}>{emphasiseNumbers(lead, t)}</div>
          {rest.map((line, i) => (
            <div key={i} style={{
              marginTop: i ? 6 : 10, fontSize: 13.5, lineHeight: 1.55,
              color: t.c.textSecondary, maxWidth: 620,
            }}>{emphasiseNumbers(line, t)}</div>
          ))}
        </motion.div>
      )}

      <div className="st-rail st-brief-notes">
        {takeaways.map((tk, i) => {
          const m = meta[tk.kind] || meta.work;
          const feat = i === featuredAt && takeaways.length > 1;
          const n = String(i + 1).padStart(2, "0");
          return (
            <motion.button
              key={tk.id || i} type="button"
              className="st-reset st-brief-note st-focusable"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: t.motion.ease.out, delay: Math.min(i * 0.05, 0.3) }}
              onClick={() => onOpen?.(tk)}
              onMouseEnter={() => onFocusStretches?.(takeawayMarks(tk))}
              onMouseLeave={() => onFocusStretches?.([])}
              style={{
                display: "grid",
                gridTemplateColumns: "36px 72px 1fr auto",
                gap: "10px 12px", alignItems: "start", width: "100%",
                padding: feat ? "16px 14px" : "14px 12px",
                margin: feat ? "4px 4px 2px" : 0,
                borderRadius: feat ? 12 : 0,
                background: feat
                  ? (t.isDark ? `${m.color}1a` : `${m.color}0f`)
                  : "transparent",
                border: feat ? `1px solid ${m.color}${t.isDark ? "40" : "2e"}` : "none",
                cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{
                fontSize: 12, fontWeight: 750, letterSpacing: "0.04em",
                color: t.c.textTertiary, fontVariantNumeric: "tabular-nums", paddingTop: 3,
              }}>{n}</span>
              <span style={{
                fontSize: 10.5, fontWeight: 750, letterSpacing: "0.08em",
                textTransform: "uppercase", color: m.color, paddingTop: 4,
              }}>{m.tag}</span>
              <span style={{ minWidth: 0 }}>
                <span style={{
                  display: "block", fontSize: feat ? 17 : 15, fontWeight: 650,
                  letterSpacing: feat ? "-0.03em" : "-0.02em", lineHeight: 1.3, color: t.c.text,
                }}>{tk.title}</span>
                {tk.detail && (
                  <span style={{
                    display: "block", marginTop: 5, fontSize: 13.5, lineHeight: 1.5,
                    color: t.c.textSecondary, maxWidth: 520,
                  }}>{tk.detail}</span>
                )}
                {feat && <NoteRibbon tk={tk} bounds={bounds} color={m.color} />}
              </span>
              <span className="st-brief-stat" style={{
                textAlign: "right", fontSize: 13, fontWeight: 700, color: m.color,
                fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", paddingTop: 2,
              }}>
                {tk.stat || ""}
                <span className="st-ins-open" style={{ display: "block", marginTop: 4, fontSize: 11.5, fontWeight: 750 }}>
                  Open →
                </span>
              </span>
            </motion.button>
          );
        })}

        {running && [0, 1, 2].map((i) => (
          <div key={`sk${i}`} style={{ display: "grid", gridTemplateColumns: "36px 72px 1fr 64px", gap: 12, padding: "14px 12px" }}>
            <Sk w={22} h={12} /><Sk w={48} h={12} /><Sk h={36} /><Sk w={56} h={14} />
          </div>
        ))}
      </div>

      {!running && !takeaways.length && !summary && (
        <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, padding: "8px 20px 16px" }}>
          No brief for this day yet.
        </div>
      )}
    </div>
  );
}
